import React, { useEffect, useState } from "react";
import { Formik, Field, FieldArray, Form } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Typography,
  Paper,
  Checkbox,
  FormControlLabel,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  createUserService,
  updatePermissionsService,
} from "../../services/authService";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import useStore from "../../useStore";

// Validation schema with Yup
const validationSchema = Yup.object({
  name: Yup.string()
    .min(3, "Name must be at least 3 characters")
    .required("Name is required"),
  phoneNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
    .required("Phone number is required"),
  email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  password: Yup.string().min(6, "Password must be at least 6 characters"),
  role: Yup.string().required("Role is required"),
  permissions: Yup.array()
    .of(
      Yup.object().shape({
        title: Yup.string().required(),
        path: Yup.string().required(),
        access: Yup.boolean(),
      })
    )
    .test(
      "at-least-one-checked",
      "Select at least one permission",
      (permissions) =>
        permissions ? permissions.some((p) => p.access === true) : false
    ),
});

// Permissions data example
let permissionsList = [
  { title: "Dashboard", path: "/dashboard", access: false },
  { title: "Customer Management", path: "/customer", access: false },
  {
    title: "Group Customer Management",
    path: "/group-customer",
    access: false,
  },
  { title: "Agent Management", path: "/agent", access: false },
  { title: "Pigmy Collections", path: "/pigmy-collections", access: false },
  { title: "Loan Collections", path: "/loan-collections", access: false },
  { title: "Group Loan", path: "/group-loan", access: false },
  { title: "Group", path: "/group", access: false },
  { title: "Group Pigmy", path: "/group-pigmy", access: false },
  { title: "Customer Report", path: "/customer-report", access: false },
  { title: "Pigmy Report", path: "/pigmy-report", access: false },
  {
    title: "Pigmy Collection Report",
    path: "/pigmy-collection-report",
    access: false,
  },
  { title: "Loan Report", path: "/loan-report", access: false },
  {
    title: "Loan Collection Report",
    path: "/loan-collection-report",
    access: false,
  },
  {
    title: "Weekly Loan Collection Report",
    path: "/weekly-loan-collection-report",
    access: false,
  },
  { title: "Group Pigmy Report", path: "/group-pigmy-report", access: false },
  {
    title: "Group Pigmy Collection Report",
    path: "/group-pigmy-collection-report",
    access: false,
  },
  { title: "Group Loan Report", path: "/group-loan-report", access: false },
  {
    title: "Group Loan Collection Report",
    path: "/group-loan-collection-report",
    access: false,
  },
  {
    title: "Weekly Group Loan Collection Report",
    path: "/weekly-group-loan-collection-report",
    access: false,
  },
  { title: "Enquiries", path: "/enquires", access: false },
  { title: "Pigmy Requests", path: "/pigmy-requests", access: false },
  {
    title: "Pigmy Withdraw Requests",
    path: "/pigmy-withdraw-requests",
    access: false,
  },
  { title: "Permissions", path: "/permissions", access: false },
  { title: "Accounts", path: "/accounts", access: false },
  { title: "Settings", path: "/settings", access: false },
  { title: "Edit Users", path: "/edit-user", access: false },
];

function CreateUser() {
  const navigate = useNavigate();
  const [editname, setEditName] = useState();
  const [editphoneNumber, setEditPhoneNumber] = useState();
  const [editemail, setEditEmail] = useState();
  const [editRole, setEditRole] = useState();
  const [showPassword, setShowPassword] = useState(false);
  const [data, setData] = useState();
  const {
    setPermissionsId,
    setIsPermissionUpdate,
    setPermissionsData,
    permissionData,
    permissionid,
    isPermissionUpdate,
  } = useStore();

  console.log(permissionid, permissionData);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  useEffect(() => {
    if (isPermissionUpdate) {
      setEditName(permissionData?.userName);
      setEditPhoneNumber(permissionData?.primaryPhone?.contactValue);
      setEditEmail(permissionData?.email);
      setEditRole(permissionData?.type);
      setData(permissionData?.permissions[0]?.permissions);
    }
  }, []);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        name: editname || "",
        phoneNumber: editphoneNumber || "",
        email: editemail || "",
        password: "", // Added password field
        role: editRole || "",
        permissions: data || permissionsList,
      }}
      validationSchema={validationSchema} // Attach the schema here
      onSubmit={async (values) => {
        console.log("Form values:", values);
        try {
          if (isPermissionUpdate) {
            const updatedValues = {
              ...values, // Spread all properties of `values`
              permissionid, // Add `editId` to the object
            };
            const updatePermission = await updatePermissionsService(
              updatedValues
            );
            if (updatePermission.status === "success") {
              toast.success("Permission updated successfully");
              // Navigate to the dashboard
              navigate("/permissions");
            }
          } else {
            const createUser = await createUserService(values); // handle form submission, e.g., API call
            if (createUser.status === "success") {
              toast.success("Customer created successfully");
              // Navigate to the dashboard
              navigate("/permissions");
            }
          }
        } catch (error) {
          toast.error(error);
        }
      }}
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form>
          <Paper sx={{ p: 3, backgroundColor: "#fff" }}>
            <Typography>Create User</Typography>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography mt={1}>Name</Typography>
                <Field
                  fullWidth
                  size="small"
                  name="name"
                  as={TextField}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography mt={1}>Phone Number</Typography>
                <Field
                  fullWidth
                  size="small"
                  name="phoneNumber"
                  as={TextField}
                  error={touched.phoneNumber && Boolean(errors.phoneNumber)}
                  helperText={touched.phoneNumber && errors.phoneNumber}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography mt={1}>Email</Typography>
                <Field
                  fullWidth
                  size="small"
                  name="email"
                  as={TextField}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
              </Grid>
              <Grid item xs={3}>
                <Typography mt={1}>Password</Typography>
                <Field
                  fullWidth
                  size="small"
                  name="password"
                  as={TextField}
                  type={showPassword ? "text" : "password"}
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={togglePasswordVisibility}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Role Dropdown */}
              <Grid item xs={3}>
                <Typography mt={1}>Role</Typography>
                <FormControl
                  fullWidth
                  size="small"
                  error={touched.role && Boolean(errors.role)}
                >
                  <Field name="role" as={Select}>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="staff">Staff</MenuItem>
                  </Field>
                  {touched.role && errors.role && (
                    <Typography color="error">{errors.role}</Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Typography mt={1}>Permissions</Typography>
                <FieldArray
                  name="permissions"
                  render={(arrayHelpers) => (
                    <Grid container>
                      {/* Select All Checkbox */}
                      <Grid item xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={values.permissions.every(
                                (perm) => perm.access
                              )}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                // Update permissions array based on "Select All" state
                                const updatedPermissions =
                                  values.permissions.map((perm) => ({
                                    ...perm,
                                    access: checked,
                                  }));
                                // Use setFieldValue to update the permissions field
                                setFieldValue(
                                  "permissions",
                                  updatedPermissions
                                );
                              }}
                            />
                          }
                          label="Select All"
                        />
                      </Grid>

                      {values.permissions.map((permission, index) => (
                        <Grid item xs={4} key={index}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={permission.access}
                                onChange={(e) =>
                                  arrayHelpers.replace(index, {
                                    ...permission,
                                    access: e.target.checked,
                                  })
                                }
                                name={`permissions[${index}].access`}
                              />
                            }
                            label={permission.title}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  )}
                />
                {errors.permissions && touched.permissions && (
                  <Typography color="error">{errors.permissions}</Typography>
                )}
              </Grid>

              <Grid item xs={12}>
                <Button type="submit" variant="contained" color="primary">
                  Submit
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Form>
      )}
    </Formik>
  );
}

export default CreateUser;
